import { queryClient } from "../queries/client";
import {
  useAuthStore,
  useAuthorizationStore,
  useCompanyStore,
  usePermissionStore,
} from "../store";
import { clearCredentials } from "../components/utils";
import { currentPageReturnUrl, loginUrl } from "../utils/returnUrl";
import { optionsAdminAuth } from "./adminAuthMode";
import { redirectAfterAdminSignOut, signOut } from "./idServer";
import optionsApi from "./optionsApi";

const channelName = "esop-options-auth";
function randomId() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
    ""
  );
}
const tabId = optionsAdminAuth ? randomId() : "";

export function clearOptionsSession() {
  clearCredentials();
  queryClient.clear();
  useAuthStore.getState().clear();
  useAuthorizationStore.getState().clearAuthorization();
  usePermissionStore.getState().clearPermission();
  useCompanyStore.getState().clearCompanyData();
}

export function redirectToOptionsLogin() {
  const target = currentPageReturnUrl();
  clearOptionsSession();
  window.location.replace(loginUrl(target));
}

export function startOptionsTokenSharing() {
  if (!optionsAdminAuth || typeof BroadcastChannel === "undefined")
    return () => {};
  const channel = new BroadcastChannel(channelName);
  channel.onmessage = (event) => {
    const data = event.data;
    if (data?.type === "logout") {
      if (data.senderId === tabId) return;
      redirectToOptionsLogin();
      return;
    }
    if (
      data?.type !== "request" ||
      data.senderId === tabId ||
      typeof data.nonce !== "string" ||
      typeof data.senderId !== "string"
    )
      return;
    const state = useAuthStore.getState();
    if (
      state.isAuthenticated &&
      /^Bearer [^\s]+$/.test(state.accessToken || "")
    ) {
      channel.postMessage({
        type: "response",
        senderId: tabId,
        targetId: data.senderId,
        nonce: data.nonce,
        token: state.accessToken,
      });
    }
  };
  return () => channel.close();
}

export function requestOptionsToken(): Promise<string | null> {
  if (typeof BroadcastChannel === "undefined") return Promise.resolve(null);
  const nonce = randomId();
  const channel = new BroadcastChannel(channelName);
  return new Promise((resolve) => {
    const finish = (token: string | null) => {
      window.clearTimeout(timer);
      channel.close();
      resolve(token);
    };
    const timer = window.setTimeout(() => finish(null), 2000);
    channel.onmessage = (event) => {
      const data = event.data;
      if (
        data?.type === "response" &&
        data.targetId === tabId &&
        data.nonce === nonce &&
        /^Bearer [^\s]+$/.test(data.token || "")
      ) {
        finish(data.token);
      }
    };
    channel.postMessage({ type: "request", senderId: tabId, nonce });
  });
}

export async function signOutAdmin(_data: unknown) {
  if (!optionsAdminAuth) return signOut({});
  await optionsApi.post("/v1/auth/logout").catch(() => undefined);
  clearOptionsSession();
  if (typeof BroadcastChannel !== "undefined") {
    const channel = new BroadcastChannel(channelName);
    channel.postMessage({ type: "logout", senderId: tabId });
    channel.close();
  }
  return { message: "Logged out successfully", data: null };
}

export function redirectAfterSignOut() {
  if (optionsAdminAuth) window.location.replace("/login");
  else redirectAfterAdminSignOut();
}
